// ToastProvider — 토스트 상태 관리 + 우하단 스택 렌더.
// App.tsx 최상단에서 한 번 마운트한다.
// 최대 5개 유지, duration=0이면 자동 닫힘 없음.

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { X } from 'lucide-react';
import { ToastContext, type ToastInput, type ToastItem, type ToastVariant } from './Toast';

// ── 아이콘 매핑 ──────────────────────────────────────────
const VARIANT_ICON: Record<ToastVariant, string> = {
  default: '',
  success: '✓',
  error:   '✕',
  warning: '⚠',
  info:    'ℹ',
};

// ── 토스트 아이템 단일 렌더 ──────────────────────────────
function ToastItemView({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: number) => void;
}) {
  const variant = item.variant ?? 'default';
  const icon = VARIANT_ICON[variant];

  return (
    <div
      className={`ui-toast ui-toast--${variant}`}
      role="status"
      aria-live="polite"
    >
      {/* 아이콘 + 텍스트 */}
      <div className="ui-toast__body">
        {icon && <span className="ui-toast__icon" aria-hidden="true">{icon}</span>}
        <div className="ui-toast__text">
          <p className="ui-toast__title">{item.title}</p>
          {item.description && (
            <p className="ui-toast__description">{item.description}</p>
          )}
        </div>
      </div>

      {/* 하단 액션 + 닫기 */}
      <div className="ui-toast__actions">
        {item.action && (
          <button
            className="ui-toast__action-btn"
            onClick={() => {
              item.action!.onClick();
              onDismiss(item.id);
            }}
          >
            {item.action.label}
          </button>
        )}
        <button
          className="ui-toast__close"
          aria-label="닫기"
          onClick={() => onDismiss(item.id)}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Provider ────────────────────────────────────────────
let _nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  // 자동 닫힘 타이머 ID 저장 (브라우저 환경이므로 number 타입)
  const timers = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const tid = timers.current.get(id);
    if (tid !== undefined) {
      clearTimeout(tid);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (input: ToastInput): number => {
      const id = ++_nextId;
      const duration = input.duration ?? 5000;

      setItems((prev) => {
        // 최대 5개 유지 (초과 시 가장 오래된 것 제거)
        const next = [...prev, { ...input, id }];
        const trimmed = next.length > 5 ? next.slice(next.length - 5) : next;
        return trimmed;
      });

      // 자동 닫힘 등록
      if (duration > 0) {
        const tid = window.setTimeout(() => dismiss(id), duration);
        timers.current.set(id, tid);
      }

      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss, items }), [push, dismiss, items]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* 우하단 토스트 스택 */}
      <div className="ui-toast-stack" aria-label="알림">
        {items.map((item) => (
          <ToastItemView key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
